import { useEffect, useState } from "react";
import { http } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Save, X, HelpCircle, CheckCircle2, Sparkles, Loader2, Check, ArrowUp, ArrowDown } from "lucide-react";

const LEVELS = [
  { key: "quiz", label: "Niveau 1", color: "#1368ce", bg: "#1368ce14" },
  { key: "difficile", label: "Niveau 2", color: "#d89e00", bg: "#d89e0014" },
  { key: "legend", label: "Légende", color: "#e21b3c", bg: "#e21b3c14" },
];
const LEVEL_MAP = Object.fromEntries(LEVELS.map(l => [l.key, l]));

export default function CoursesAdmin() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [courses, setCourses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [intros, setIntros] = useState({});

  async function loadCourses() {
    const r = await http.get("/courses");
    setCourses(r.data.sort((a,b)=>a.order-b.order));
  }
  async function loadQuestions(cid) {
    const r = await http.get(`/admin/questions?course_id=${cid}`);
    setQuestions(r.data);
  }
  async function loadIntros() {
    const r = await http.get("/intros").catch(()=>({data:[]}));
    const map = {}; r.data.forEach(i => map[i.competency] = i.text);
    setIntros(map);
  }

  useEffect(() => { loadCourses(); loadIntros(); }, []);
  useEffect(() => { if (selected) loadQuestions(selected); else setQuestions([]); }, [selected]);

  async function deleteCourse(cid) {
    if (!window.confirm(`Supprimer le cours ${cid} et toutes ses questions ?`)) return;
    try { await http.delete(`/admin/courses/${cid}`); toast.success("Cours supprimé"); loadCourses(); if (selected === cid) setSelected(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  }

  async function deleteQuestion(qid) {
    if (!window.confirm("Supprimer cette question ?")) return;
    try {
      await http.delete(`/admin/questions/${qid}`);
      await loadQuestions(selected);
      toast.success("Question supprimée");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        await loadQuestions(selected);
        toast.info("Cette question n'existait plus — la liste est rafraîchie.");
      } else {
        toast.error(e?.response?.data?.detail || "Erreur de suppression");
      }
    }
  }

  async function bulkDeleteQuestions(ids) {
    if (!ids?.length) return;
    if (!window.confirm(`Supprimer ${ids.length} question(s) ?`)) return;
    try {
      const r = await http.post("/admin/questions/bulk-delete", { ids });
      await loadQuestions(selected);
      toast.success(`${r.data.deleted} question(s) supprimée(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  }

  async function deleteAllQuestions(level) {
    const label = level === "all" ? "toutes les questions" : `toutes les questions du niveau ${level}`;
    if (!window.confirm(`Supprimer ${label} de ce cours ?`)) return;
    try {
      const r = await http.post("/admin/questions/bulk-delete", { course_id: selected, level });
      await loadQuestions(selected);
      toast.success(`${r.data.deleted} question(s) supprimée(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  }

  async function moveCourse(cid, direction) {
    const current = courses.find(c => c.id === cid);
    if (!current) return;
    const siblings = courses.filter(c => c.competency === current.competency).sort((a,b)=>a.order-b.order);
    const idx = siblings.findIndex(c => c.id === cid);
    const neighborIdx = direction === "up" ? idx - 1 : idx + 1;
    if (neighborIdx < 0 || neighborIdx >= siblings.length) return;
    const neighbor = siblings[neighborIdx];
    try {
      await Promise.all([
        http.patch(`/admin/courses/${current.id}`, { order: neighbor.order }),
        http.patch(`/admin/courses/${neighbor.id}`, { order: current.order }),
      ]);
      toast.success("Ordre mis à jour");
      loadCourses();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de réorganisation"); }
  }

  async function saveIntro(comp, text) {
    await http.put("/admin/intros", { competency: comp, text });
    toast.success(`Intro C${comp} sauvegardée`); loadIntros();
  }

  return (
    <div className="space-y-6" data-testid="courses-admin-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-headings font-black text-3xl md:text-4xl text-[#1a2332]">Administration des cours</h1>
          <p className="mt-1 text-[#5b6577]">{courses.length} cours · gestion du catalogue et du contenu pédagogique</p>
        </div>
        {isSuper && (
          <button onClick={() => { setEditingCourse(null); setShowCourseForm(true); }} data-testid="new-course-btn"
            className="inline-flex items-center gap-2 bg-[#FF8C42] hover:bg-[#e8763a] text-white font-headings font-bold rounded-xl px-5 py-2.5 shadow-sm">
            <Plus className="w-4 h-4" /> Nouveau cours
          </button>
        )}
      </div>

      {showCourseForm && isSuper && (
        <CourseForm key={editingCourse?.id || "new"} initial={editingCourse} onClose={() => setShowCourseForm(false)} onDone={() => { loadCourses(); setShowCourseForm(false); }} />
      )}

      {/* Competency intros — super_admin only */}
      {isSuper && (
        <div className="grid md:grid-cols-2 gap-4">
          {[1,2].map(c => <IntroEditor key={c} comp={c} value={intros[c] || ""} onSave={saveIntro} />)}
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 bg-white border border-black/5 rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 bg-[#f8faff] font-headings font-bold text-[#1a2332] border-b border-black/5">Catalogue</div>
          <div className="max-h-[60vh] overflow-y-auto">
            {[1,2,3,4].map(comp => (
              <div key={comp}>
                <div className="px-4 py-2 text-xs font-headings font-bold text-[#00a86b] bg-[#f3f5fa]">Compétence {comp}</div>
                {courses.filter(c => c.competency === comp).sort((a,b)=>a.order-b.order).map((c, i, arr) => (
                  <div key={c.id} data-testid={`admin-course-${c.id}`}
                    className={`px-4 py-3 border-b border-black/5 flex items-center gap-3 cursor-pointer hover:bg-[#f8faff] ${selected === c.id ? "bg-[#00a86b]/5" : ""}`}
                    onClick={() => setSelected(c.id)}>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-mono text-[#9aa5bc]">{`C${c.competency}-${String(c.order).padStart(2, "0")}`}</div>
                      <div className="font-headings font-semibold text-[#1a2332] text-sm truncate">{c.title}</div>
                    </div>
                    {isSuper && (
                      <>
                        <div className="flex flex-col gap-0.5">
                          <button onClick={(e) => { e.stopPropagation(); moveCourse(c.id, "up"); }}
                            disabled={i === 0}
                            className="p-0.5 rounded hover:bg-[#00a86b]/10 text-[#00a86b] disabled:opacity-30 disabled:cursor-not-allowed"
                            aria-label="Monter" data-testid={`move-up-${c.id}`}>
                            <ArrowUp className="w-3 h-3" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); moveCourse(c.id, "down"); }}
                            disabled={i === arr.length - 1}
                            className="p-0.5 rounded hover:bg-[#00a86b]/10 text-[#00a86b] disabled:opacity-30 disabled:cursor-not-allowed"
                            aria-label="Descendre" data-testid={`move-down-${c.id}`}>
                            <ArrowDown className="w-3 h-3" />
                          </button>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); setEditingCourse(c); setShowCourseForm(true); }}
                          className="p-1.5 rounded hover:bg-[#00a86b]/10 text-[#00a86b]" aria-label="Modifier"
                          data-testid={`edit-course-${c.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); deleteCourse(c.id); }}
                          className="p-1.5 rounded hover:bg-[#e21b3c]/10 text-[#e21b3c]" aria-label="Supprimer"
                          data-testid={`delete-course-${c.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          {selected ? (
            <QuestionsPanel cid={selected} course={courses.find(c => c.id === selected)} questions={questions}
              onDelete={deleteQuestion} onBulkDelete={bulkDeleteQuestions} onDeleteAll={deleteAllQuestions}
              reload={() => loadQuestions(selected)} />
          ) : (
            <div className="bg-white border border-black/5 rounded-2xl shadow-sm p-10 text-center text-[#5b6577]">
              <HelpCircle className="w-10 h-10 mx-auto text-[#00a86b]/40 mb-3" />
              Sélectionnez un cours à gauche pour gérer ses questions.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CourseForm({ initial, onClose, onDone }) {
  const isEdit = !!initial;
  const [f, setF] = useState(initial || { id: "", competency: 1, order: 1, title: "", has_quiz: true, key_points: "", content: "", video_url: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        const { id, ...rest } = f;
        await http.patch(`/admin/courses/${initial.id}`, rest);
        toast.success("Cours mis à jour");
      } else {
        await http.post("/admin/courses", f);
        toast.success("Cours créé");
      }
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  return (
    <form onSubmit={submit} className="bg-white border border-black/5 rounded-2xl shadow-sm p-5 grid md:grid-cols-6 gap-3" data-testid="course-form">
      <input required placeholder="ID (ex: c1-12)" disabled={isEdit} value={f.id} onChange={e=>setF({...f,id:e.target.value})} className="input-admin md:col-span-2" data-testid="course-id" />
      <input required type="number" min={1} max={4} placeholder="Compétence" value={f.competency} onChange={e=>setF({...f,competency:+e.target.value})} className="input-admin" data-testid="course-comp" />
      <input required type="number" placeholder="Ordre" value={f.order} onChange={e=>setF({...f,order:+e.target.value})} className="input-admin" data-testid="course-order" />
      <label className="inline-flex items-center gap-2 text-sm md:col-span-2">
        <input type="checkbox" checked={f.has_quiz} onChange={e=>setF({...f,has_quiz:e.target.checked})} /> A un quiz
      </label>
      <input required placeholder="Titre" value={f.title} onChange={e=>setF({...f,title:e.target.value})} className="input-admin md:col-span-6" data-testid="course-title" />
      <input placeholder="URL vidéo YouTube (https://www.youtube.com/watch?v=… ou https://youtu.be/…) — optionnel" value={f.video_url||""} onChange={e=>setF({...f,video_url:e.target.value})} className="input-admin md:col-span-6" data-testid="course-video-url" />
      <textarea rows={2} placeholder="Points clés (résumé court)" value={f.key_points||""} onChange={e=>setF({...f,key_points:e.target.value})} className="input-admin md:col-span-6 resize-none" data-testid="course-keypoints" />
      <textarea rows={8} placeholder="Contenu pédagogique complet — texte affiché à l'élève quand il ouvre le cours…" value={f.content||""} onChange={e=>setF({...f,content:e.target.value})} className="input-admin md:col-span-6 resize-y" data-testid="course-content" />
      <div className="md:col-span-6 flex gap-2">
        <button className="bg-[#FF8C42] text-white font-headings font-bold rounded-lg px-4 py-2 text-sm" data-testid="course-save">{isEdit ? "Mettre à jour" : "Créer"}</button>
        <button type="button" onClick={onClose} className="bg-[#f8faff] border border-black/5 text-[#1a2332] rounded-lg px-4 py-2 text-sm">Annuler</button>
      </div>
      <style>{`.input-admin{background:#f8faff;border:1px solid #e5eaf3;color:#1a2332;padding:.55rem .75rem;border-radius:.6rem;font-size:.85rem;}`}</style>
    </form>
  );
}

function IntroEditor({ comp, value, onSave }) {
  const [text, setText] = useState(value);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setText(value); setDirty(false); }, [value]);
  return (
    <div className="bg-white border border-black/5 rounded-2xl shadow-sm p-4" data-testid={`intro-editor-${comp}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-headings font-bold text-[#1a2332]">Introduction Compétence {comp}</div>
        {dirty && <button onClick={() => onSave(comp, text)} className="text-xs inline-flex items-center gap-1 bg-[#00a86b] text-white rounded-lg px-3 py-1 font-headings font-bold"><Save className="w-3 h-3"/> Enregistrer</button>}
      </div>
      <textarea rows={3} value={text} onChange={e=>{setText(e.target.value); setDirty(true);}}
        className="w-full bg-[#f8faff] border border-[#e5eaf3] rounded-lg p-2 text-sm resize-none text-[#1a2332]"
        placeholder="Texte d'introduction affiché aux élèves pour cette compétence…" />
    </div>
  );
}

function QuestionsPanel({ cid, course, questions, onDelete, onBulkDelete, onDeleteAll, reload }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [genOpen, setGenOpen] = useState(false);
  const [count, setCount] = useState(5);
  const [genLevel, setGenLevel] = useState("quiz");
  const [filterLevel, setFilterLevel] = useState("all");
  const [loading, setLoading] = useState(false);
  const [proposals, setProposals] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Reset selection when the course or filter changes
  useEffect(() => { setSelectedIds(new Set()); }, [cid, filterLevel]);

  async function generate() {
    setLoading(true);
    const attempt = async () => {
      const r = await http.post("/admin/questions/generate", { course_id: cid, count, level: genLevel });
      return (r.data?.proposals || []);
    };
    try {
      let items = [];
      try { items = await attempt(); }
      catch (e) {
        // One retry on transient errors (502/timeout)
        if (e?.response?.status >= 500 || !e?.response) {
          toast.info("Nouvelle tentative…");
          items = await attempt();
        } else { throw e; }
      }
      if (items.length === 0) { toast.error("Aucune question générée — réessayez."); }
      else {
        setProposals(items.map(p => ({ ...p, selected: true })));
        toast.success(`${items.length} questions générées`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de génération IA");
    } finally { setLoading(false); }
  }

  async function saveSelected() {
    const picked = (proposals || []).filter(p => p.selected).map(({ selected, ...rest }) => rest);
    if (picked.length === 0) { toast.error("Sélectionnez au moins une question."); return; }
    try {
      const r = await http.post("/admin/questions/bulk-save", { course_id: cid, level: genLevel, questions: picked });
      toast.success(`${r.data.count} question(s) enregistrée(s)`);
      setProposals(null); setGenOpen(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'enregistrement"); }
  }

  const filteredQuestions = filterLevel === "all" ? questions : questions.filter(q => (q.level || "quiz") === filterLevel);
  const countByLevel = LEVELS.reduce((acc, l) => { acc[l.key] = questions.filter(q => (q.level || "quiz") === l.key).length; return acc; }, {});

  return (
    <div className="bg-white border border-black/5 rounded-2xl shadow-sm overflow-hidden">
      <div className="p-4 flex items-center justify-between bg-[#f8faff] border-b border-black/5 flex-wrap gap-2">
        <div className="font-headings font-bold text-[#1a2332]">Questions du quiz — <span className="text-[#5b6577]">{course ? `C${course.competency}-${String(course.order).padStart(2,"0")}` : cid.toUpperCase()}</span> · {questions.length}</div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setGenOpen(v => !v); setProposals(null); }} data-testid="open-ai-generator-btn"
            className="inline-flex items-center gap-1 bg-gradient-to-r from-[#FF8C42] to-[#e8763a] text-white font-headings font-bold rounded-lg px-3 py-1.5 text-xs shadow">
            <Sparkles className="w-3.5 h-3.5" /> Générer par IA
          </button>
          <button onClick={() => { setEditing({ __new_type: "mcq" }); setShowForm(true); }} data-testid="new-question-btn"
            className="inline-flex items-center gap-1 bg-[#00a86b] text-white font-headings font-bold rounded-lg px-3 py-1.5 text-xs">
            <Plus className="w-3 h-3" /> Nouveau QCM
          </button>
          <button onClick={() => { setEditing({ __new_type: "fill" }); setShowForm(true); }} data-testid="new-fill-btn"
            className="inline-flex items-center gap-1 bg-[#1368ce] text-white font-headings font-bold rounded-lg px-3 py-1.5 text-xs">
            <Plus className="w-3 h-3" /> Phrase à trou
          </button>
        </div>
      </div>

      {genOpen && (
        <div className="p-4 border-b border-black/5 bg-gradient-to-br from-[#fff7f0] to-white" data-testid="ai-generator-panel">
          {!proposals && (
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <div className="text-xs text-[#5b6577] mb-1">Niveau de difficulté</div>
                <div className="flex gap-1 bg-white border border-[#e5eaf3] rounded-lg p-1" data-testid="ai-level-tabs">
                  {LEVELS.map(l => (
                    <button key={l.key} type="button" onClick={() => setGenLevel(l.key)} data-testid={`ai-level-${l.key}`}
                      className={`text-xs font-headings font-bold rounded-md px-3 py-1.5 transition ${genLevel === l.key ? "text-white" : "text-[#5b6577] hover:bg-[#f8faff]"}`}
                      style={genLevel === l.key ? { backgroundColor: l.color } : {}}>
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs text-[#5b6577] mb-1">Nombre de questions</div>
                <input type="number" min={1} max={20} value={count} onChange={e=>setCount(Math.max(1, Math.min(20, +e.target.value || 5)))}
                  className="bg-white border border-[#e5eaf3] rounded-lg px-3 py-2 w-24 text-sm text-[#1a2332]" data-testid="ai-count-input" />
              </div>
              <button onClick={generate} disabled={loading} data-testid="ai-generate-btn"
                className="inline-flex items-center gap-2 bg-[#FF8C42] hover:bg-[#e8763a] text-white font-headings font-bold rounded-lg px-4 py-2 text-sm disabled:opacity-60">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {loading ? "Génération en cours…" : "Lancer la génération"}
              </button>
              <button onClick={() => setGenOpen(false)}
                className="inline-flex items-center gap-1 bg-white border border-black/5 text-[#1a2332] rounded-lg px-3 py-2 text-sm">
                <X className="w-3.5 h-3.5" /> Fermer
              </button>
              <p className="text-xs text-[#5b6577] w-full mt-1">L'IA calibre les questions et les explications sur le niveau choisi. Vous pourrez ensuite sélectionner, modifier puis enregistrer celles à conserver.</p>
              {genLevel === "difficile" && (
                <p className="text-xs text-[#FF8C42] w-full font-headings font-semibold flex items-center gap-1" data-testid="ai-mirror-hint">
                  ✨ À l'enregistrement, ces questions seront <u>aussi créées en Légende</u> (même contenu, seul le temps change : 25 s → 15 s).
                </p>
              )}
            </div>
          )}
          {proposals && (
            <div className="space-y-3" data-testid="ai-review-list">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm text-[#5b6577] flex items-center gap-2">
                  <span className="text-xs font-headings font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: LEVEL_MAP[genLevel]?.color }}>{LEVEL_MAP[genLevel]?.label}</span>
                  {proposals.filter(p=>p.selected).length} / {proposals.length} sélectionnée(s)
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setProposals(proposals.map(p => ({...p, selected: true})))}
                    className="text-xs bg-white border border-black/5 text-[#1a2332] rounded-lg px-3 py-1">Tout sélectionner</button>
                  <button onClick={() => setProposals(proposals.map(p => ({...p, selected: false})))}
                    className="text-xs bg-white border border-black/5 text-[#1a2332] rounded-lg px-3 py-1">Tout désélectionner</button>
                  <button onClick={() => setProposals(null)}
                    className="text-xs bg-white border border-black/5 text-[#1a2332] rounded-lg px-3 py-1">Régénérer</button>
                  <button onClick={saveSelected} data-testid="ai-save-selected-btn"
                    className="text-xs inline-flex items-center gap-1 bg-[#00a86b] hover:bg-[#008855] text-white font-headings font-bold rounded-lg px-3 py-1">
                    <Check className="w-3.5 h-3.5" /> Enregistrer les sélectionnées
                  </button>
                </div>
              </div>
              {proposals.map((p, i) => (
                <ProposalCard key={i} idx={i} proposal={p} onChange={(next) =>
                  setProposals(proposals.map((x,j) => j===i ? next : x))
                } />
              ))}
            </div>
          )}
        </div>
      )}

      {showForm && (
        <QuestionForm key={editing?.id || "new"} cid={cid} initial={editing} onClose={() => setShowForm(false)}
          onDone={() => { setShowForm(false); reload(); }} />
      )}

      {/* Level filter tabs + bulk actions */}
      <div className="px-4 py-2 bg-white border-b border-black/5 flex items-center gap-2 flex-wrap" data-testid="level-filter-tabs">
        <div className="flex items-center gap-1 overflow-x-auto">
          <button onClick={() => setFilterLevel("all")} data-testid="filter-all"
            className={`text-xs font-headings font-bold rounded-full px-3 py-1 whitespace-nowrap ${filterLevel === "all" ? "bg-[#1a2332] text-white" : "bg-[#f8faff] text-[#5b6577] hover:bg-[#e5eaf3]"}`}>
            Toutes · {questions.length}
          </button>
          {LEVELS.map(l => (
            <button key={l.key} onClick={() => setFilterLevel(l.key)} data-testid={`filter-${l.key}`}
              className="text-xs font-headings font-bold rounded-full px-3 py-1 whitespace-nowrap transition"
              style={filterLevel === l.key ? { backgroundColor: l.color, color: "white" } : { backgroundColor: l.bg, color: l.color }}>
              {l.label} · {countByLevel[l.key]}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {filteredQuestions.length > 0 && (
            <>
              <label className="inline-flex items-center gap-1.5 text-xs text-[#5b6577] cursor-pointer" data-testid="select-all-label">
                <input type="checkbox"
                  checked={filteredQuestions.length > 0 && filteredQuestions.every(q => selectedIds.has(q.id))}
                  onChange={(e) => {
                    const next = new Set(selectedIds);
                    if (e.target.checked) filteredQuestions.forEach(q => next.add(q.id));
                    else filteredQuestions.forEach(q => next.delete(q.id));
                    setSelectedIds(next);
                  }}
                  data-testid="select-all-checkbox" />
                Tout sélectionner
              </label>
              {selectedIds.size > 0 && (
                <button onClick={() => { onBulkDelete(Array.from(selectedIds)); setSelectedIds(new Set()); }}
                  data-testid="bulk-delete-selected-btn"
                  className="inline-flex items-center gap-1 bg-[#e21b3c] hover:bg-[#c41632] text-white font-headings font-bold rounded-lg px-3 py-1 text-xs">
                  <Trash2 className="w-3 h-3" /> Supprimer ({selectedIds.size})
                </button>
              )}
              <button onClick={() => onDeleteAll(filterLevel)}
                data-testid="delete-all-btn"
                className="inline-flex items-center gap-1 bg-white border border-[#e21b3c]/30 text-[#e21b3c] hover:bg-[#e21b3c]/5 font-headings font-bold rounded-lg px-3 py-1 text-xs">
                <Trash2 className="w-3 h-3" /> {filterLevel === "all" ? "Tout supprimer" : `Supprimer ${LEVEL_MAP[filterLevel]?.label}`}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="max-h-[55vh] overflow-y-auto divide-y divide-black/5">
        {filteredQuestions.length === 0 && (
          <div className="p-8 text-center text-[#5b6577] text-sm">
            {questions.length === 0 ? <>Aucune question. Utilisez <strong>Générer par IA</strong> ou <strong>Nouvelle question</strong>.</> : <>Aucune question pour ce niveau — sélectionnez le niveau dans le générateur IA.</>}
          </div>
        )}
        {filteredQuestions.map((q, i) => {
          const lvl = LEVEL_MAP[q.level || "quiz"];
          const isChecked = selectedIds.has(q.id);
          return (
          <div key={q.id || i} className={`p-4 ${isChecked ? "bg-[#FF8C42]/5" : ""}`} data-testid={`question-${q.id}`}>
            <div className="flex items-start justify-between gap-3">
              <input type="checkbox" checked={isChecked} onChange={(e) => {
                const next = new Set(selectedIds);
                if (e.target.checked) next.add(q.id); else next.delete(q.id);
                setSelectedIds(next);
              }} className="mt-1.5 shrink-0" data-testid={`select-question-${q.id}`} />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-xs text-[#9aa5bc]">#{i+1}</div>
                  <span className="text-[10px] font-headings font-bold uppercase px-2 py-0.5 rounded-full" style={{ backgroundColor: lvl.bg, color: lvl.color }}>{lvl.label}</span>
                  <span className={`text-[10px] font-headings font-bold uppercase px-2 py-0.5 rounded-full ${q.type === "fill" ? "bg-[#1368ce]/10 text-[#1368ce]" : "bg-[#00a86b]/10 text-[#00a86b]"}`}>
                    {q.type === "fill" ? "Phrase à trou" : "QCM"}
                  </span>
                </div>
                {q.type === "fill" ? (
                  <>
                    <div className="font-semibold text-[#1a2332]">
                      {(q.text || "").split("__").map((part, k, arr) => (
                        <span key={k}>
                          {part}
                          {k < arr.length - 1 && (
                            <span className="px-2 py-0.5 mx-1 rounded bg-[#1368ce]/10 text-[#1368ce] font-mono text-xs">___</span>
                          )}
                        </span>
                      ))}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5 items-center">
                      <span className="text-[11px] text-[#5b6577]">Réponses :</span>
                      {(q.blanks || []).map((b, j) => (
                        <span key={j} className="text-xs px-2 py-0.5 rounded bg-[#00a86b]/10 text-[#007a4e] font-semibold">{j+1}. {b}</span>
                      ))}
                    </div>
                    {(q.distractors || []).length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5 items-center">
                        <span className="text-[11px] text-[#5b6577]">Pièges :</span>
                        {q.distractors.map((d, j) => (
                          <span key={j} className="text-xs px-2 py-0.5 rounded bg-[#e21b3c]/10 text-[#c41632]">{d}</span>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="font-semibold text-[#1a2332]">{q.q}</div>
                    <div className="text-[11px] text-[#5b6577] mt-1 mb-1.5 italic">
                      Cliquez sur une réponse pour la définir comme correcte.
                    </div>
                    <div className="mt-1 grid gap-1.5">
                      {(q.choices || []).map((c, j) => (
                        <button key={j} type="button"
                          onClick={async () => {
                            if (j === q.correct) return;
                            try {
                              await http.patch(`/admin/questions/${q.id}`, { correct: j });
                              toast.success(`Bonne réponse : ${String.fromCharCode(65+j)}`);
                              reload();
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || "Erreur de mise à jour");
                            }
                          }}
                          data-testid={`set-correct-${q.id}-${j}`}
                          title={j === q.correct ? "Bonne réponse actuelle" : "Cliquer pour marquer comme bonne réponse"}
                          className={`text-left text-xs rounded px-2 py-1.5 border transition cursor-pointer ${j === q.correct ? "bg-[#00a86b]/10 border-[#00a86b]/30 text-[#007a4e] font-semibold" : "bg-[#f8faff] border-black/5 text-[#5b6577] hover:bg-[#00a86b]/5 hover:border-[#00a86b]/30"}`}>
                          <div className="flex items-start gap-2">
                            <span className={`shrink-0 w-4 h-4 rounded-full border-2 mt-0.5 flex items-center justify-center ${j === q.correct ? "border-[#00a86b] bg-[#00a86b]" : "border-[#9aa5bc] bg-white"}`}>
                              {j === q.correct && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                            </span>
                            <span className="flex-1"><strong>{String.fromCharCode(65+j)}.</strong> {c}</span>
                          </div>
                          {q.explanations?.[j] && (
                            <div className="mt-1 pl-6 text-[11px] italic text-[#5b6577]">{q.explanations[j]}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEditing(q); setShowForm(true); }} className="p-1.5 rounded hover:bg-[#00a86b]/10 text-[#00a86b]" data-testid={`edit-question-${q.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => onDelete(q.id)} className="p-1.5 rounded hover:bg-[#e21b3c]/10 text-[#e21b3c]" data-testid={`delete-question-${q.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

function ProposalCard({ idx, proposal, onChange }) {
  const p = proposal;
  const setField = (k, v) => onChange({ ...p, [k]: v });
  const setChoice = (j, v) => onChange({ ...p, choices: p.choices.map((c, jj) => jj === j ? v : c) });
  const setExpl = (j, v) => {
    const expls = [...(p.explanations || [])];
    while (expls.length < p.choices.length) expls.push("");
    expls[j] = v;
    onChange({ ...p, explanations: expls });
  };
  return (
    <div className={`rounded-xl border p-3 bg-white ${p.selected ? "border-[#00a86b]/40 ring-1 ring-[#00a86b]/30" : "border-black/10 opacity-70"}`} data-testid={`proposal-${idx}`}>
      <div className="flex items-start gap-2 mb-2">
        <input type="checkbox" checked={!!p.selected} onChange={e=>setField("selected", e.target.checked)}
          className="mt-1" data-testid={`proposal-check-${idx}`} />
        <div className="text-xs font-mono text-[#9aa5bc] mt-1">#{idx+1}</div>
        <textarea rows={2} value={p.q} onChange={e=>setField("q", e.target.value)}
          className="flex-1 bg-[#f8faff] border border-[#e5eaf3] rounded-lg p-2 text-sm text-[#1a2332] resize-none font-semibold"
          data-testid={`proposal-q-${idx}`} />
      </div>
      <div className="space-y-2 pl-6">
        {p.choices.map((c, j) => (
          <div key={j} className="space-y-1">
            <div className="flex items-center gap-2">
              <input type="radio" name={`proposal-correct-${idx}`} checked={p.correct === j} onChange={()=>setField("correct", j)}
                data-testid={`proposal-correct-${idx}-${j}`} />
              <span className="text-xs font-bold text-[#1a2332] w-5">{String.fromCharCode(65+j)}.</span>
              <input value={c} onChange={e=>setChoice(j, e.target.value)}
                className={`flex-1 border rounded-lg px-2 py-1.5 text-sm ${p.correct === j ? "bg-[#00a86b]/5 border-[#00a86b]/30 text-[#007a4e] font-semibold" : "bg-white border-[#e5eaf3] text-[#1a2332]"}`}
                data-testid={`proposal-choice-${idx}-${j}`} />
            </div>
            <textarea rows={2} placeholder={`Explication pour la réponse ${String.fromCharCode(65+j)}…`}
              value={(p.explanations || [])[j] || ""} onChange={e=>setExpl(j, e.target.value)}
              className="w-full ml-7 bg-[#fff7f0] border border-[#FF8C42]/20 rounded-lg p-2 text-xs text-[#5b3a1a] resize-none italic"
              style={{width: "calc(100% - 1.75rem)"}}
              data-testid={`proposal-expl-${idx}-${j}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

function QuestionForm({ cid, initial, onClose, onDone }) {
  const isEdit = !!initial && !!initial.id;
  const initialType = (initial?.type) || (initial?.__new_type) || "mcq";
  const [type, setType] = useState(initialType);

  // MCQ state
  const initChoices = initial?.choices ? [...initial.choices, ...Array(4 - initial.choices.length).fill("")].slice(0, 4) : ["", "", "", ""];
  const initExpl = initial?.explanations ? [...initial.explanations, ...Array(4 - initial.explanations.length).fill("")].slice(0, 4) : ["", "", "", ""];
  const [mcq, setMcq] = useState({
    q: initial?.q || "",
    choices: initChoices,
    correct: initial?.correct ?? 0,
    explanations: initExpl,
  });

  // Fill state
  const [fill, setFill] = useState({
    text: initial?.text || "",
    blanks: initial?.blanks || [],
    distractors: initial?.distractors || [],
  });
  const [distractorInput, setDistractorInput] = useState("");

  const [level, setLevel] = useState(initial?.level || "quiz");

  // Helper to insert a __ blank for the selected word
  function blankSelectedWord() {
    const ta = document.getElementById("fill-text-area");
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    if (start === end) { toast.error("Sélectionnez le mot à masquer dans le texte."); return; }
    const word = fill.text.slice(start, end).trim();
    if (!word) return;
    const newText = fill.text.slice(0, start) + "__" + fill.text.slice(end);
    setFill({ ...fill, text: newText, blanks: [...fill.blanks, word] });
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + 2, start + 2); }, 50);
  }

  function removeBlank(i) {
    // Find the i-th __ in text and replace with the original blank word
    let occurrence = 0;
    const newText = fill.text.replace(/__/g, (m) => {
      const replacement = (occurrence === i) ? (fill.blanks[i] || "") : m;
      occurrence += 1;
      return replacement;
    });
    setFill({ ...fill, text: newText, blanks: fill.blanks.filter((_, j) => j !== i) });
  }

  function addDistractor() {
    const v = distractorInput.trim();
    if (!v) return;
    if (fill.distractors.includes(v) || fill.blanks.includes(v)) { toast.error("Mot déjà présent."); return; }
    setFill({ ...fill, distractors: [...fill.distractors, v] });
    setDistractorInput("");
  }

  const submit = async (e) => {
    e.preventDefault();
    try {
      let payload;
      if (type === "fill") {
        const text = fill.text.trim();
        const numBlanks = (text.match(/__/g) || []).length;
        if (!text) { toast.error("Le texte est requis."); return; }
        if (numBlanks === 0) { toast.error("Sélectionnez au moins un mot à masquer."); return; }
        if (numBlanks !== fill.blanks.length) { toast.error(`Incohérence : ${numBlanks} trous mais ${fill.blanks.length} mots.`); return; }
        payload = { type: "fill", text, blanks: fill.blanks, distractors: fill.distractors, level };
      } else {
        const rawChoices = mcq.choices;
        const kept = []; const keptExpls = [];
        rawChoices.forEach((c, i) => { if (c.trim()) { kept.push(c.trim()); keptExpls.push((mcq.explanations[i] || "").trim()); } });
        if (kept.length < 2) { toast.error("Au moins 2 réponses sont requises."); return; }
        if (!mcq.q.trim()) { toast.error("La question est requise."); return; }
        const originalCorrectText = rawChoices[mcq.correct]?.trim();
        const newCorrectIdx = originalCorrectText ? kept.indexOf(originalCorrectText) : -1;
        if (newCorrectIdx === -1) { toast.error("La bonne réponse est vide."); return; }
        payload = { type: "mcq", q: mcq.q.trim(), choices: kept, correct: newCorrectIdx, explanations: keptExpls, level };
      }
      if (isEdit) {
        await http.patch(`/admin/questions/${initial.id}`, payload);
        toast.success("Question mise à jour");
      } else {
        await http.post("/admin/questions", { course_id: cid, ...payload });
        toast.success("Question créée");
      }
      onDone();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404 && isEdit) { toast.error("Question disparue — liste rafraîchie."); onDone(); }
      else { toast.error(err?.response?.data?.detail || "Erreur lors de l'enregistrement"); }
    }
  };

  return (
    <form onSubmit={submit} className="p-5 border-b border-black/5 bg-[#f8faff] space-y-3" data-testid="question-form">
      {/* Type tabs */}
      <div className="flex gap-1 bg-white border border-[#e5eaf3] rounded-lg p-1 w-fit">
        <button type="button" onClick={() => setType("mcq")} data-testid="form-type-mcq"
          className={`text-xs font-headings font-bold rounded-md px-3 py-1.5 ${type === "mcq" ? "bg-[#00a86b] text-white" : "text-[#5b6577]"}`}>
          QCM
        </button>
        <button type="button" onClick={() => setType("fill")} data-testid="form-type-fill"
          className={`text-xs font-headings font-bold rounded-md px-3 py-1.5 ${type === "fill" ? "bg-[#1368ce] text-white" : "text-[#5b6577]"}`}>
          Phrase à trou
        </button>
      </div>

      {/* Level */}
      <div>
        <div className="text-xs text-[#5b6577] mb-1">Niveau</div>
        <div className="flex gap-1 bg-white border border-[#e5eaf3] rounded-lg p-1 w-fit">
          {LEVELS.map(l => (
            <button key={l.key} type="button" onClick={() => setLevel(l.key)} data-testid={`question-level-${l.key}`}
              className="text-xs font-headings font-bold rounded-md px-3 py-1 transition"
              style={level === l.key ? { backgroundColor: l.color, color: "white" } : { color: "#5b6577" }}>
              {l.label}
            </button>
          ))}
        </div>
      </div>

      {type === "mcq" ? (
        <>
          <input required placeholder="Question" value={mcq.q} onChange={e=>setMcq({...mcq, q:e.target.value})}
            className="input-admin w-full" data-testid="question-q" />
          <div className="text-xs text-[#5b6577]">Cochez la bonne réponse · 2 à 4 réponses · explication pour chaque.</div>
          {mcq.choices.map((c, i) => (
            <div key={i} className="space-y-1.5 border border-black/5 rounded-lg p-2 bg-white">
              <div className="flex items-center gap-2">
                <input type="radio" name="correct" checked={mcq.correct === i} onChange={()=>setMcq({...mcq, correct:i})}
                  data-testid={`question-correct-${i}`} />
                <span className="text-xs font-bold text-[#1a2332] w-5">{String.fromCharCode(65+i)}.</span>
                <input placeholder={`Réponse ${i+1}${i < 2 ? " (obligatoire)" : " (optionnel)"}`} value={c}
                  onChange={e=>setMcq({...mcq, choices: mcq.choices.map((cc,j)=>j===i?e.target.value:cc)})}
                  className="input-admin flex-1" data-testid={`question-choice-${i}`} />
              </div>
              <textarea rows={2} placeholder={`Explication pour la réponse ${String.fromCharCode(65+i)}`}
                value={mcq.explanations[i] || ""}
                onChange={e=>setMcq({...mcq, explanations: mcq.explanations.map((ee,j)=>j===i?e.target.value:ee)})}
                className="input-admin w-full resize-none italic" data-testid={`question-expl-${i}`} />
            </div>
          ))}
        </>
      ) : (
        <>
          <div>
            <div className="text-xs text-[#5b6577] mb-1.5">Texte de la phrase à trou</div>
            <p className="text-[11px] text-[#9aa5bc] mb-1.5">
              ↳ Tapez ou collez votre texte. <strong>Sélectionnez un mot</strong> et cliquez <strong>« Masquer ce mot »</strong> pour le transformer en trou.
            </p>
            <textarea id="fill-text-area" required rows={4} placeholder="Ex : Le airbag est un dispositif de sécurité obligatoire dans tous les véhicules."
              value={fill.text} onChange={e=>setFill({...fill, text:e.target.value})}
              className="input-admin w-full resize-y" data-testid="fill-text" />
            <div className="mt-1.5 flex gap-2">
              <button type="button" onClick={blankSelectedWord} data-testid="blank-word-btn"
                className="bg-[#1368ce] text-white font-headings font-bold rounded-lg px-3 py-1.5 text-xs">
                Masquer le mot sélectionné →
              </button>
              <span className="text-[11px] text-[#9aa5bc] self-center">{(fill.text.match(/__/g) || []).length} trou(s) · {fill.blanks.length} mot(s)</span>
            </div>
          </div>

          {fill.blanks.length > 0 && (
            <div>
              <div className="text-xs text-[#5b6577] mb-1.5">Mots à trouver dans l'ordre</div>
              <div className="flex flex-wrap gap-1.5">
                {fill.blanks.map((b, j) => (
                  <span key={j} className="inline-flex items-center gap-1 text-xs bg-[#00a86b]/10 text-[#007a4e] font-semibold rounded-full pl-2 pr-1 py-0.5">
                    {j+1}. {b}
                    <button type="button" onClick={() => removeBlank(j)} className="ml-1 hover:bg-[#e21b3c]/20 rounded-full w-4 h-4 flex items-center justify-center text-[#e21b3c]" title="Retirer" data-testid={`remove-blank-${j}`}>×</button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="text-xs text-[#5b6577] mb-1.5">Mots pièges (distracteurs) — affichés dans la banque mais aucune solution</div>
            <div className="flex gap-2">
              <input value={distractorInput} onChange={e=>setDistractorInput(e.target.value)}
                onKeyDown={e=>{ if (e.key==="Enter") { e.preventDefault(); addDistractor(); }}}
                placeholder="Ajouter un mot piège puis Entrée" className="input-admin flex-1" data-testid="distractor-input" />
              <button type="button" onClick={addDistractor} className="bg-[#FF8C42] text-white font-headings font-bold rounded-lg px-4 py-2 text-xs">Ajouter</button>
            </div>
            {fill.distractors.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {fill.distractors.map((d, j) => (
                  <span key={j} className="inline-flex items-center gap-1 text-xs bg-[#e21b3c]/10 text-[#c41632] font-semibold rounded-full pl-2 pr-1 py-0.5">
                    {d}
                    <button type="button" onClick={() => setFill({...fill, distractors: fill.distractors.filter((_, k) => k !== j)})}
                      className="ml-1 hover:bg-[#e21b3c]/30 rounded-full w-4 h-4 flex items-center justify-center" data-testid={`remove-distractor-${j}`}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <div className="flex gap-2">
        <button className="bg-[#00a86b] text-white font-headings font-bold rounded-lg px-4 py-2 text-sm inline-flex items-center gap-1" data-testid="question-save">
          <Save className="w-3.5 h-3.5" /> {isEdit ? "Mettre à jour" : "Créer"}
        </button>
        <button type="button" onClick={onClose} className="bg-white border border-black/5 text-[#1a2332] rounded-lg px-4 py-2 text-sm inline-flex items-center gap-1">
          <X className="w-3.5 h-3.5" /> Annuler
        </button>
      </div>
      <style>{`.input-admin{background:#fff;border:1px solid #e5eaf3;color:#1a2332;padding:.55rem .75rem;border-radius:.6rem;font-size:.85rem;}`}</style>
    </form>
  );
}
